import React, { useState, useEffect, useCallback, useRef } from 'react'

// Deps
import { Calendar, DateRange } from 'react-date-range';
import format from 'date-fns/format';
import tr from 'date-fns/locale/tr'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import omit from 'lodash/omit'
import debounce from 'lodash/debounce'

const useValueStrings = (value, displayFormat, dateFormat, rangeMode, startDateFormat, endDateFormat) => {
	if(rangeMode) {
		if(value && value.length >= 2 && value[0] && value[1]) {
			return ([
				format(value[0], displayFormat, { locale: tr }) + ' - ' + format(value[1], displayFormat, { locale: tr }),
				[format(value[0], (startDateFormat ? startDateFormat : dateFormat), { locale: tr }), format(value[1], (endDateFormat ? endDateFormat : dateFormat), { locale: tr })],
			]);
		}
		else return [false, ['', '']];
	}
	else {
		if(value && value !== '') {
			return ([
				format(value, displayFormat, { locale: tr }),
				format(value, dateFormat, { locale: tr }),
			]);
		}
		else return [false, ''];
	}
}

const DatePicker = (props) => {
	const range = props.range;

	const _mounted = useRef(false);

	const [elemProps, setElemProps] = useState({});
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState(false);
	const [initialSelection, setInitialSelection] = useState(false);

	const [displayValue, dataValue] = useValueStrings(value, props.displayFormat, props.dateFormat, props.range, props.startDateFormat, props.endDateFormat);

	const [focused, setFocused] = useState(false);
	const [focusState, setFocusState] = useState(false);

	const focusChange = useCallback(debounce((state) => {
		if(_mounted) {
			if(_mounted.current) {
				if(!state) {
					setOpen(false);
					setInitialSelection(false);
				}
				setFocused(state);
			}
		}
	}, 100, {leading: false}), []);

	const handleChange = useCallback((value) => {
		if(_mounted.current) {
			if(range) {
				setValue([value.selection.startDate, value.selection.endDate]);

				if(initialSelection || (value.selection.startDate !== value.selection.endDate)) {
					setInitialSelection(false);

					setTimeout(() => {
						if(_mounted.current) {
							setOpen(false);
							setFocusState(false);
						}
					}, 500);
				}
				else {
					setInitialSelection(true);
				}
			}
			else {
				setValue(value);

				setTimeout(() => {
					if(_mounted.current) {
						setOpen(false);
						setFocusState(false);
					}
				}, 500);
			}
		}
	}, [range, initialSelection])

	const valueChange = useCallback(debounce((value) => {
		if(_mounted) {
			props.onChange(value);
		}
	}, 100, {leading: false}), [props.onChange, focused]);

	useEffect(() => {
		focusChange(focusState);
	}, [focusState, focusChange]);

	useEffect(() => {
		valueChange(value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, valueChange]);

	useEffect(() => {
		setValue(props.value);
	}, [props.value])

	useEffect(() => {
		setElemProps({
			...omit(props, ['dateFormat', 'onChange', 'id', 'value', 'range', 'name', 'startDateName', 'endDateName']),
			startDatePlaceholder: 'Başlangıç',
			endDatePlaceholder: 'Bitiş',
			onChange: handleChange,
			locale: tr,
			inputRanges: [],
			staticRanges: [],

		});

	}, [props, handleChange])

	useEffect(() => {
		_mounted.current = true;

		return(() => {
			_mounted.current = false;
		})
	}, []);

	return (
		<div
			className={'datepicker' + (open ? ' open' : '')}>
			<button
				type="button"
				id={props.id}
				onFocus={() => { setFocusState(true); }}
				onBlur={() => { setFocusState(false); }}
				onClick={() => { setOpen(true); }}
				className={'datepicker-button inputwrap-input' + (focused ? ' focused' : '')}>
				{displayValue ?
					<React.Fragment>
						{displayValue}
					</React.Fragment>
					:
					<React.Fragment>
						{props.placeholder &&
							<span className="button-placeholder">{props.placeholder}</span>
						}
					</React.Fragment>
				}
			</button>
			{!range &&
				<input
					type="hidden"
					disabled={props.disabled ? true : undefined}
					name={props.name}
					value={dataValue} />
			}
			{(range && props.startDateName) &&
				<input
					type="hidden"
					disabled={props.disabled ? true : undefined}
					name={props.startDateName}
					value={dataValue[0]} />
			}
			{(range && props.endDateName) &&
				<input
					type="hidden"
					disabled={props.disabled ? true : undefined}
					name={props.endDateName}
					value={dataValue[1]} />
			}
			{(open) &&
				<div
					onFocus={() => { setFocusState(true); }}
					onBlur={() => { setFocusState(false); }}
					className="datepicker-calendarwrap">
					{props.range ?
						<DateRange
							showSelectionPreview={true}
							ranges={[{
								startDate: (value && value.length ? value[0] : undefined),
								endDate: (value && value.length ? value[1] : undefined),
								key: 'selection',
							}]}
							months={2}
							{...elemProps} />
						:
						<Calendar
							date={value ? value : undefined}
							{...elemProps} />

					}
				</div>
			}
		</div>
	)
}

DatePicker.defaultProps = {
	//dateFormat: 'yyyy-MM-dd HH:mm',
	value: null,
	dateFormat: 'yyyy-MM-dd',
	startDateFormat: false,
	endDateFormat: false,
	displayFormat: 'dd MMMM yyyy',
	color: '#3e475f',
}

export default DatePicker